import { Injectable } from '@angular/core';
import { paaInitialState, PaaStateProps, paaStateStore } from './paa-state.store';
import { select } from '@ngneat/elf';
import { BaseStoreRepository } from '../base-store.repository';
import { PaaMilestoneDto, Paa, PaaDto, SlotDto, FundingDto } from '@/types';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class PaaRepository extends BaseStoreRepository<PaaStateProps> {
  constructor() {
    super(paaStateStore.pipe(select(state => state)), paaStateStore, paaInitialState);
  }

  paaFields: (keyof PaaDto)[] = ['nom', 'acId', 'annee', 'remarque'];

  milestoneFields: (keyof PaaMilestoneDto)[] = [
    'realisationEnvisagees',
    'beneficiaire',
    'typeMarcher.id',
    'modePassation.id',
    'imputationBudgetaire.id',
    'budgetEstime',
  ];

  milestoneDates: (keyof PaaMilestoneDto)[] = ['datePrevLps'];

  setResponseAndDto(response: Paa) {
    this.set('response', response);
    this.set('dto', {
      ref: response.ref,
      nom: response.nom,
      acId: response.ac.id,
      annee: response.annee,
      paaFiles: [],
      docIds: response.docs.map(doc => doc.id),
      remarque: response.remarque,
    });
    this.set('initialDto', structuredClone(this.get('dto')));
    this.set('acsSelected', { name: response.ac.nom, code: response.ac.id });
  }

  getCreatePayload(milestoneDtos: PaaMilestoneDto[]) {
    const data: Record<string, any> = {};
    const formData = new FormData();

    // Append global fields if available
    this.paaFields.forEach(field => {
      const value = this.getNested('dto', field);
      if (value !== undefined && value !== null) {
        formData.append(field, String(value));
        data[field] = value;
      }
    });

    const realisationsLotsJson: Record<string, SlotDto[]> = {};
    const realisationsFinancementsJson: Record<string, FundingDto[]> = {};

    // Append milestone-related fields
    milestoneDtos.forEach((milestone, index) => {
      this.milestoneFields.forEach(field => {
        const value = milestone[field];
        if (value) formData.append(`realisations[${index}].${field}`, String(value));
      });
      this.milestoneDates.forEach(field => {
        const value = milestone[field]?.toString();
        if (value) formData.append(`realisations[${index}].${field}`, format(new Date(value), 'yyyy-MM-dd'));
      });
      milestone.realisationFiles.forEach(file => {
        formData.append(`realisations[${index}].realisationFiles`, file);
      });
      realisationsLotsJson[index.toString()] = milestone.lots;
      realisationsFinancementsJson[index.toString()] = milestone.financements;
    });

    // Append lots
    formData.append('realisationsLotsJson', JSON.stringify(realisationsLotsJson));

    // Append financements
    formData.append('realisationsFinancementsJson', JSON.stringify(realisationsFinancementsJson));

    // Append files
    this.getNested<File[]>('dto', 'paaFiles').forEach(file => {
      formData.append('paaFiles', file);
    });

    return { data, formData };
  }

  getUpdatePayload(milestoneDtos: PaaMilestoneDto[], prefix = '') {
    const data: Record<string, any> = {};
    const formData = new FormData();

    // Append global fields if available
    this.paaFields.forEach(field => {
      const value = this.getNested('dto', field);
      if (value !== undefined && value !== null) {
        formData.append(`${prefix}${field}`, String(value));
        data[field] = value;
      }
    });

    const realisationsLotsJson: Record<string, SlotDto[]> = {};
    const realisationsFinancementsJson: Record<string, FundingDto[]> = {};

    milestoneDtos.forEach((milestone, index) => {
      if (milestone.id) {
        formData.append(`${prefix}realisations[${index}].id`, String(milestone.id));
        formData.append(`${prefix}realisations[${index}].docIds`, milestone.docIds.join(','));
      }
      this.milestoneFields.forEach(field => {
        const value = milestone[field];
        if (value) formData.append(`${prefix}realisations[${index}].${field}`, String(value));
      });
      this.milestoneDates.forEach(field => {
        const value = milestone[field]?.toString();
        if (value) formData.append(`${prefix}realisations[${index}].${field}`, format(new Date(value), 'yyyy-MM-dd'));
      });
      milestone.realisationFiles?.forEach(file => {
        formData.append(`${prefix}realisations[${index}].realisationFiles`, file);
      });

      realisationsFinancementsJson[index.toString()] = milestone.financements.map(financement => {
        if (financement.id)
          return {
            id: financement.id,
            sourceFinancement: financement.sourceFinancement,
            budgetEstimer: financement.budgetEstimer,
            monnaieId: financement.monnaieId,
          };
        else return { sourceFinancement: financement.sourceFinancement, budgetEstimer: financement.budgetEstimer, monnaieId: financement.monnaieId };
      });

      realisationsLotsJson[index.toString()] = milestone.lots.map(lot => {
        if (lot.id) return { id: lot.id, description: lot.description, budget: lot.budget };
        else return { description: lot.description, budget: lot.budget };
      });
    });

    // Append files
    this.getNested<File[]>('dto', 'paaFiles').forEach(file => {
      formData.append(`${prefix}paaFiles`, file);
    });
    formData.append(`${prefix}docIds`, this.getNested<number[]>('dto', 'docIds').join(','));

    // Append financements
    formData.append(`realisationsFinancementsJson`, JSON.stringify(realisationsFinancementsJson));

    // Append lots
    formData.append(`realisationsLotsJson`, JSON.stringify(realisationsLotsJson));
    return { data, formData };
  }
}
